import React, { useEffect, useState, useRef, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { VideoCallIcon } from "../../components/icons/Icons";
import {
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  format,
  startOfWeek,
  addDays,
  isSameDay,
  startOfDay,
  endOfDay,
  isToday,
  isSameMonth,
  endOfWeek
} from "date-fns";
import styles from "./Appointment.module.css";

function CalendarView({
  currentWeek,
  viewType,
  appointments,
  onAppointmentClick,
  physicians,
}) {
  const timelineRef = useRef(null);
  const now = new Date(); // Get current date and time
  const nowMinus15 = new Date(now.getTime() - 15 * 60 * 1000);
  const currentMeetings = useSelector((state) => state.appointment.currentMeetings).flat();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupAppointments, setPopupAppointments] = useState([]);
  const [popupDay, setPopupDay] = useState([]);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [currentTime, setCurrentTime] = useState(new Date());

  const prevApptColors = {font:"#373737", background: "#D6D6D6", border:"#686869"};
  const futureApptColors = {font:"#00162E", background: "#B2C7CE", border:"#1D5769"};

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const startHour = 6; // 8 AM
  const endHour = 24; // 11 PM
  const headerHeight = 90;

  const monthViewRef = useRef(null);
  const dayNamesRef = useRef(null);

  useLayoutEffect(() => {
    if (viewType !== "Month") return;
    function adjustForScrollbar() {
      const monthView = monthViewRef.current;
      const dayNames = dayNamesRef.current;
      if (monthView.scrollHeight > monthView.clientHeight) {
        dayNames.style.paddingRight = '7px';
      } else {
        dayNames.style.paddingRight = '0';
      }
    }

    adjustForScrollbar(); // Run initially
    window.addEventListener('resize', adjustForScrollbar);
    const observer = new MutationObserver(adjustForScrollbar);
    if (monthViewRef.current) {
      observer.observe(monthViewRef.current, { childList: true, subtree: true });
    }

    // Clean up the event listener on component unmount
    return () => {
      window.removeEventListener('resize', adjustForScrollbar);
      observer.disconnect();
    };
  }, [viewType, appointments]);

  const getCurrentTimePosition = () => {
    const currentHour = currentTime.getHours();
    const currentMinutes = currentTime.getMinutes();
  
    // Check if current time is within the calendar range
    if (currentHour < startHour) {
      return headerHeight; // Place at the top (8 AM)
    } else if (currentHour >= endHour) {
      return (endHour - startHour ) * 150 + headerHeight; // Place at the bottom (11 PM)
    }
  
    // Calculate minutes since startHour (8 AM)
    const minutesSinceStartOfDay = (currentHour - startHour) * 60 + currentMinutes;
  
    // Update: Each hour slot is now 150px tall
    const pixelsPerMinute = 150 / 60; // 1 minute = 2.5px
    return minutesSinceStartOfDay * pixelsPerMinute + headerHeight;
  };
  
  useEffect(() => {
    if (timelineRef.current) {
      timelineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentWeek, viewType]);

  const renderCurrentTimeLine = () => {
    const position = getCurrentTimePosition();

    return (
      <div
        ref={timelineRef}
        style={{
          position: 'absolute',
          top: `${position}px`,
          left: '0',
          right: '0',
          height: '2px',
          backgroundColor: 'rgba(29,87,105,1)',
          zIndex: 8,
        }}
      />
    );
  };


  const handleMoreClick = (additionalAppointments, event, day) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popupHeight = 43 + (28*additionalAppointments.length);
    const positionAbove = rect.bottom + popupHeight > window.innerHeight;;
    const popupWidth = 250;
    const positionLeft = rect.right + popupWidth > window.innerWidth;;
    setPopupPosition({
      top: positionAbove ? rect.top + window.scrollY - 10 - (popupHeight*2) : rect.bottom + window.scrollY, // Position at the bottom of the day element or top if too close to bottom
      left: positionLeft ? rect.left - 150 : rect.right // Position at the right edge of the day element
    });
    setPopupDay(day);
    setPopupAppointments(additionalAppointments);
    setIsPopupOpen(true);
  };

  const renderPopup = () => {
    if (!isPopupOpen) return null;

    const handlePopupClick = (appointment) => {
      setIsPopupOpen(false);
      onAppointmentClick(appointment); 
    };
  
    return (
      <div className={styles.appointmentPopupOverlay} onClick={() => setIsPopupOpen(false)}>
        <div
          className={styles.appointmentPopup}
          style={{ top: `${popupPosition.top}px`, left: `${popupPosition.left}px` }}
        >
          <span className={styles.appointmentPopupDate}>{format(popupDay, "eeee MMM d")}</span>
          {popupAppointments.map(appointment => {
            const startTime = format(new Date(appointment.appointmentStartDateTime), 'h:mm a');
            return (
              <div key={appointment.appointmentId}
                  className={styles.appointmentPopupItem}
                  onClick={() => handlePopupClick(appointment)}>
                {`${startTime} - ${appointment.patientName}, ${getPhysicianNameById(appointment.physicianId, appointment)}`}
              </div>
            )
          })}
        </div>
      </div>
    );
  };

  const getPhysicianNameById = (physicianId, appointment = null) => {
    if (appointment?.staffName) {
      return appointment.staffName;
    }
    if (appointment?.staffFirstName && appointment?.staffLastName) {
      const roleName = appointment.title;
      return roleName === 'Doctor' 
        ? `Dr. ${appointment.staffFirstName} ${appointment.staffLastName}`
        : `${appointment.staffFirstName} ${appointment.staffLastName}`;
    }
    const physician = physicians.find(
      (p) => p.value.toString() === physicianId.toString()
    );
    if (physician) {
      return physician.label;
    }
    console.warn(`[CalendarView] Could not find physician name for ID ${physicianId}. Appointment data:`, {
      staffName: appointment?.staffName,
      staffFirstName: appointment?.staffFirstName,
      staffLastName: appointment?.staffLastName,
      title: appointment?.title,
      physicianId: appointment?.physicianId
    });
    return "Unknown Physician";
  };

  function calculateAppointmentPositions(appointments) {
    let lastEndTimes = [];
    let columnsOccupied = [];
    let updatedAppointments = appointments.map(appointment => ({
      ...appointment, // Spread to copy properties
    }));
  
    updatedAppointments.sort(
      (a, b) =>
        new Date(a.appointmentStartDateTime) - new Date(b.appointmentStartDateTime)
    );
  
    updatedAppointments.forEach((appointment, index) => {
      const appointmentStart = new Date(appointment.appointmentStartDateTime);
      const appointmentEnd = new Date(appointment.appointmentEndDateTime);
      let placed = false;
  
      for (let i = 0; i < lastEndTimes.length; i++) {
        if (appointmentStart >= lastEndTimes[i]) {
          lastEndTimes[i] = appointmentEnd;
          updatedAppointments[index] = {
            ...appointment,
            column: i,
          };
          columnsOccupied[i] = columnsOccupied[i] || [];
          columnsOccupied[i].push({
            start: appointmentStart,
            end: appointmentEnd,
          });
          placed = true;
          break;
        }
      }
  
      if (!placed) {
        lastEndTimes.push(appointmentEnd);
        updatedAppointments[index] = {
          ...appointment,
          column: lastEndTimes.length - 1,
        };
        columnsOccupied[updatedAppointments[index].column] = [
          { start: appointmentStart, end: appointmentEnd },
        ];
      }
    });
  
    // Updating spanAll and totalColumns properties after initial placement
    const maxColumns = lastEndTimes.length;
    updatedAppointments = updatedAppointments.map(appointment => {
      const appointmentStart = new Date(appointment.appointmentStartDateTime);
      const appointmentEnd = new Date(appointment.appointmentEndDateTime);
      const overlap = columnsOccupied.some((column, index) => {
        return (
          column !== columnsOccupied[appointment.column] &&
          column.some(
            timeSlot =>
              appointmentStart < timeSlot.end && appointmentEnd > timeSlot.start
          )
        );
      });
  
      return {
        ...appointment,
        spanAll: !overlap && maxColumns > 1,
        totalColumns: maxColumns,
      };
    });
  
    return updatedAppointments; // Return new array of updated appointment objects
  }
  

  const renderWeeklyView = () => {
    const startDate = startOfWeek(currentWeek, { weekStartsOn: 0 });
    const daysOfWeek = Array.from({ length: 7 }, (_, index) =>
      addDays(startDate, index)
    );
    const hours = generateHours(6, 23);

    return (
        <div className={styles.calendarView}>
          <div className={styles.hourColumn}>
            <div className={styles.dayHeaderPlaceholder}></div>
            {hours.map((hour, index) => (
              <div key={index} className={`${styles.hourSlot} ${styles.borderLeft}`}>
                {hour.formatted}
              </div>
            ))}
          </div>

          {daysOfWeek.map((day, index) => {
            const dayAppointments = appointments.filter((appointment) =>
              isSameDay(new Date(appointment.appointmentStartDateTime), day)
            );

            // Calculate positions for the entire day's appointments
            const calculatedAppts = calculateAppointmentPositions(dayAppointments);

            return (
              <div key={day} 
                className={`${styles.dayColumn} ${isToday(day) ? styles.dayColumnToday : ''} ${index===6 && styles.borderRight}`}>
                <div className={isToday(day) ? styles.dayHeaderToday : styles.dayHeader} style={isToday(day) ? {background: "hsl(195, 27%, 93%)"} : {}}>
                  <div
                    className={styles.dayHeaderInfo}
                    style={{ textAlign: "center" }}
                  >
                    <div className={styles.dayHeaderFirst}>
                      {format(day, "EEE")}
                    </div>
                    <div className={styles.dayHeaderSecond}>
                      {format(day, "d")}
                    </div>
                  </div>
                </div>

                {renderCurrentTimeLine()}

                {hours.map((hour, index) => {
                  const hourAppointments = calculatedAppts.filter(
                    (appointment) =>
                      new Date(
                        appointment.appointmentStartDateTime
                      ).getHours() === hour.numeric
                  );

                  return (
                    <div
                      key={index}
                      className={styles.hourSlot}
                      style={{ position: "relative", height: "150px", borderLeft:"none" }}
                    >
                      {hourAppointments.map((appointment) => {
                        const startMinutes = new Date(
                          appointment.appointmentStartDateTime
                        ).getMinutes();
                        const durationInMinutes =
                          (new Date(appointment.appointmentEndDateTime) -
                            new Date(appointment.appointmentStartDateTime)) /
                          60000;

                        // Check if appointment is in currentMeetings (patient in waiting room)
                        // Only use currentMeetings for real-time state - this is updated via WebSocket
                        const isInCurrentMeetings = currentMeetings.some(
                          (meeting) =>
                            meeting.appointmentId === appointment.appointmentId
                        );
                        // Show "in waiting room" ONLY if it's in currentMeetings (real-time WebSocket state)
                        // Don't rely on appointment.isActive as it may be stale from backend
                        let isInMeeting = isInCurrentMeetings;
                        const endDateTime = new Date(appointment.appointmentEndDateTime);
                        const hasPassed = endDateTime < nowMinus15;

                        const meetingColor = hasPassed ? prevApptColors.background
                          : isInMeeting
                          ? "#1D5769"
                          : futureApptColors.background;
                        const textColor = hasPassed ? prevApptColors.font 
                          : isInMeeting
                          ? "white" 
                          : futureApptColors.font;
                        const borderColor = hasPassed ? prevApptColors.border : futureApptColors.border;
                        const heightPercentage = (durationInMinutes / 60) * 100;
                        const shouldHideText = heightPercentage <= 10; // Determines if the text should be hidden

                        return (
                          <div key={appointment.appointmentId}>
                            <div
                              // key={appointment.appointmentId}
                              className={`${styles.appointment} ${
                                shouldHideText ? styles.hideText : ""
                              }`}
                              onClick={() => onAppointmentClick(appointment)}
                              style={{
                                borderLeft: `3px solid ${borderColor}`,
                                backgroundColor: meetingColor,
                                top: `${(startMinutes / 60) * 100}%`,
                                height: `${heightPercentage}%`,
                                position: "absolute",
                                left: `${
                                  appointment.spanAll
                                    ? 0
                                    : (appointment.column /
                                        appointment.totalColumns) *
                                      100
                                }%`,
                                color: textColor,
                                width: `${
                                  appointment.spanAll
                                    ? "100%"
                                    : `${100 / appointment.totalColumns}%`
                                }`,
                              }}
                            >
                              {shouldHideText && (
                                <span className={styles.ellipsis}>...</span>
                              )}
                              {isInMeeting ? (
                                   <div className={styles.appointmentDetailsWaitingRoom}>         
                                    <div style={{flexShrink:"0"}}>
                                    <VideoCallIcon />
                                    </div>              
                                    <span className={styles.inMeetingMsg}>
                                      {shouldHideText ? "" : `${appointment.patientName} in waiting room`}
                                    </span>
                                  </div>
                                ) :
                                <div className={styles.appointmentDetails}>
                                  {appointment.patientName} <br />
                                  {getPhysicianNameById(appointment.physicianId, appointment)}                              
                                </div>
                                }
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
    );
  };

  const renderMonthView = () => {
    const start = startOfWeek(startOfMonth(currentWeek), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(currentWeek), { weekStartsOn: 1 });
    const days = eachDayOfInterval({ start, end });
  
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  
    return (
      <div style={{ width: "100%", height: "100%"}}>
        <div className={styles.dayNames} ref={dayNamesRef}>
          {dayNames.map((name) => (
            <div key={name} className={styles.monthDayName}>{name}</div>
          ))}
        </div>
        <div className={styles.monthView} ref={monthViewRef}>
          {days.map((day) => {
            const dayAppointments = appointments.filter((appointment) =>
              isSameDay(new Date(appointment.appointmentStartDateTime), day)
            ).sort(
              (a, b) =>
                new Date(a.appointmentStartDateTime) -
                new Date(b.appointmentStartDateTime)
            );

            const hasMoreAppts = dayAppointments.length > 3;
            const appointmentsShown = dayAppointments.slice(0,3);
            const additionalAppointments = dayAppointments.slice(3);
  
            const currentMonth = isSameMonth(startOfMonth(currentWeek), day);

            return (
              <div key={day} className={`${isToday(day) ? styles.daySquareToday : styles.daySquare} ${!currentMonth && styles.otherMonth}`}>
                <div className={styles.dateHeader}>{format(day, "d")}</div>
                {/* Render all appointments without slicing */}
                {appointmentsShown.map((appointment) => {
                  const endDateTime = new Date(appointment.appointmentEndDateTime);
                  const startTime = format(new Date(appointment.appointmentStartDateTime), 'h:mm a'); // Formats time as '10:00 AM'
                  const hasPassed = endDateTime < nowMinus15;
                  return (
                    <div
                      key={appointment.appointmentId}
                      onClick={() => onAppointmentClick(appointment)}
                      className={styles.appointmentMonth}
                      style={{
                        borderLeft: `3px solid ${hasPassed ? prevApptColors.border : futureApptColors.border}`,
                        backgroundColor: hasPassed ? prevApptColors.background : futureApptColors.background,
                        color: hasPassed ? prevApptColors.font : futureApptColors.font,
                      }}
                    >
                      <div className={styles.appointmentMonthDetails}>
                        {`${startTime} - ${appointment.patientName} -`}
                        <div style={{fontWeight:"400", paddingLeft:"2px"}}>{getPhysicianNameById(appointment.physicianId, appointment)}</div>
                      </div>
                    </div>
                  );
                })}
                {hasMoreAppts &&
                  <div className={styles.moreAppointments} onClick={(e)=>handleMoreClick(additionalAppointments, e, day)}>
                    {`${additionalAppointments.length} more`}
                  </div>
                }

              </div>
            );
          })}
        </div>
        {renderPopup()}
      </div>
    );
  };
  
  

  const renderDayView = () => {
    const dayStart = startOfDay(currentWeek);
    const dayEnd = endOfDay(dayStart);
    const hours = generateHours(6, 23);

    const dayAppointments = appointments.filter(
      (appointment) =>
        new Date(appointment.appointmentStartDateTime) >= dayStart &&
        new Date(appointment.appointmentStartDateTime) <= dayEnd
    );

    const columns = calculateAppointmentPositions(dayAppointments);

    return (
      <div className={styles.calendarView}>
        {/* Hour Column */}
        <div className={styles.hourColumn}>
          <div className={styles.dayHeaderPlaceholder}></div>
          {hours.map((hour, index) => (
            <div key={index} className={`${styles.hourSlot} ${styles.borderLeft}`}>
              {hour.formatted}
            </div>
          ))}
        </div>
        {/* Day Column */}
        <div className={`${styles.dayColumn} ${styles.borderRight}`}>
          <div className={isToday(currentWeek) ? styles.dayHeaderToday : styles.dayHeader}>
            <div
              className={styles.dayHeaderInfo}
              style={{ textAlign: "center" }}
            >
              <div className={styles.dayHeaderFirst}>
                {format(currentWeek, "EEEE")}
              </div>
              <div className={styles.dayHeaderSecond}>
                {format(currentWeek, "d")}
              </div>
            </div>
          </div>

          {renderCurrentTimeLine()}
          
          {hours.map((hour) => {
            const hourAppointments = dayAppointments.filter(
              (appointment) =>
                new Date(appointment.appointmentStartDateTime).getHours() ===
                hour.numeric
            );

            return (
              <div
                key={hour.numeric}
                className={styles.hourSlot}
                style={{ position: "relative", height: "150px" }}
              >
                {hourAppointments.map((appointment) => {
                  const startMinutes = new Date(
                    appointment.appointmentStartDateTime
                  ).getMinutes();
                  const durationInMinutes =
                    (new Date(appointment.appointmentEndDateTime) -
                      new Date(appointment.appointmentStartDateTime)) /
                    60000;
                  // Check if appointment is in currentMeetings (patient in waiting room)
                  // Only use currentMeetings for real-time state - this is updated via WebSocket
                  const isInCurrentMeetings = currentMeetings.some(
                    (meeting) =>
                      meeting.appointmentId === appointment.appointmentId
                  );
                  // Show "in waiting room" ONLY if it's in currentMeetings (real-time WebSocket state)
                  // Don't rely on appointment.isActive as it may be stale from backend
                  let isInMeeting = isInCurrentMeetings;
                  

                  const endDateTime = new Date(appointment.appointmentEndDateTime);
                  const hasPassed = endDateTime < nowMinus15;

                  const meetingColor = hasPassed ? prevApptColors.background
                    : isInMeeting ? "#1D5769"
                    : futureApptColors.background;
                  const textColor =  hasPassed ? prevApptColors.font
                    : isInMeeting ? "white" 
                    : futureApptColors.font;
                  const borderColor = hasPassed ? prevApptColors.border : futureApptColors.border;
                  const heightPercentage = (durationInMinutes / 60) * 100;
                  const shouldHideText = heightPercentage <= 10; // Determines if the text should be hidden

                  return (
                    <div key={appointment.appointmentId}>
                      <div
                        className={`${styles.appointment} ${
                          shouldHideText ? styles.hideText : ""
                        }`}
                        onClick={() => onAppointmentClick(appointment)}
                        style={{
                          borderLeft: `3px solid ${borderColor}`,
                          backgroundColor: meetingColor,
                          top: `${(startMinutes / 60) * 100}%`,
                          height: `${heightPercentage}%`,
                          position: "absolute",
                          left: `${
                            appointment.spanAll
                              ? 0
                              : (appointment.column /
                                  appointment.totalColumns) *
                                100
                          }%`,
                          color: textColor,
                          width: `${
                            appointment.spanAll
                              ? "100%"
                              : `${100 / appointment.totalColumns}%`
                          }`,
                        }}
                      >
                        {shouldHideText && (
                          <span className={styles.ellipsis}>...</span>
                        )}
                        {isInMeeting ? (
                          <div className={styles.appointmentDetailsWaitingRoom}>         
                            <div style={{flexShrink:"0"}}>
                            <VideoCallIcon />
                            </div>              
                            <span className={styles.inMeetingMsg}>
                              {`${appointment.patientName} in waiting room`}
                            </span>
                          </div>
                          ) :
                          <div className={styles.appointmentDetails}>
                            {appointment.patientName} <br />
                            {getPhysicianNameById(appointment.physicianId, appointment)}                              
                          </div>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const generateHours = (startHour, endHour) => {
    const formatHour = (hour) => ({
      numeric: hour,
      formatted: new Date(0, 0, 0, hour).toLocaleTimeString("en-US", {
        hour: "numeric",
        hour12: true,
      }),
    });

    const hours = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      hours.push(formatHour(hour));
    }

    return hours;
  };

  switch (viewType) {
    case "Day":
      return renderDayView();
    case "Week":
      return renderWeeklyView();
    case "Month":
      return renderMonthView();
    default:
      return renderWeeklyView();
  }
}

export default CalendarView;
